import {calculateErrorMessage} from 'common/helpers'

export function detectOpenModal(): HTMLElement | null {
  try {
    const bootstrapModal = document.querySelector('.modal.show') as HTMLElement
    if (bootstrapModal) {
      const style = window.getComputedStyle(bootstrapModal)
      if (
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        style.opacity !== '0' &&
        bootstrapModal.getAttribute('aria-hidden') !== 'true'
      ) {
        return bootstrapModal
      }
    }

    const backdrop = document.querySelector('.modal-backdrop') as HTMLElement
    if (backdrop) {
      const modal = document.querySelector(
        '.modal.show, .modal[style*="display: block"]'
      ) as HTMLElement
      if (modal) return modal
    }

    const dialogs = document.querySelectorAll('[role="dialog"]')
    for (const dialog of dialogs) {
      const el = dialog as HTMLElement
      const closestModal = el.closest('.modal') as HTMLElement | null
      if (closestModal) {
        const modalStyle = window.getComputedStyle(closestModal)
        if (
          modalStyle.display !== 'none' &&
          modalStyle.visibility !== 'hidden' &&
          modalStyle.opacity !== '0' &&
          closestModal.getAttribute('aria-hidden') !== 'true' &&
          closestModal.classList.contains('show')
        ) {
          return closestModal
        }
      }
      const style = window.getComputedStyle(el)
      if (
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        style.opacity !== '0' &&
        el.offsetParent !== null &&
        el.getAttribute('aria-hidden') !== 'true'
      ) {
        return el
      }
    }

    const modalPatterns = ['.offcanvas.show', '.drawer.open', '.swal2-container']
    for (const pattern of modalPatterns) {
      const modal = document.querySelector(pattern) as HTMLElement
      if (
        modal &&
        modal.offsetParent !== null &&
        window.getComputedStyle(modal).opacity !== '0' &&
        modal.getAttribute('aria-hidden') !== 'true'
      ) {
        return modal
      }
    }

    const allElements = document.querySelectorAll('*')
    for (const el of allElements) {
      const element = el as HTMLElement
      const style = window.getComputedStyle(element)
      const zIndex = parseInt(style.zIndex) || 0

      if (
        (style.position === 'fixed' || style.position === 'absolute') &&
        zIndex > 1000 &&
        element.offsetWidth > window.innerWidth * 0.3 &&
        element.offsetHeight > window.innerHeight * 0.3 &&
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        style.opacity !== '0' &&
        element.offsetParent !== null
      ) {
        if (!element.id?.includes('walkthrough') && !element.className?.includes('walkthrough')) {
          return element
        }
      }
    }

    return null
  } catch (err) {
    calculateErrorMessage(err)
    return null
  }
}

export async function waitForModalClose(
  modalElement: HTMLElement,
  timeoutMs: number = 30000
): Promise<boolean> {
  const normalizedElement =
    (modalElement.closest('.modal') as HTMLElement | null) ||
    (modalElement.closest('.offcanvas') as HTMLElement | null) ||
    modalElement
  const start = Date.now()
  const interval = 300
  let resolved = false

  return new Promise((resolve) => {
    const listeners: Array<{
      el: HTMLElement | Window
      event: string
      handler: EventListener
    }> = []

    const cleanup = () => {
      listeners.forEach(({el, event, handler}) => {
        try {
          el.removeEventListener(event, handler)
        } catch (e) {
          calculateErrorMessage(e)
        }
      })
    }

    const resolveClose = () => {
      if (resolved) return
      resolved = true
      cleanup()
      resolve(true)
    }

    const resolveTimeout = () => {
      if (resolved) return
      resolved = true
      cleanup()
      resolve(false)
    }

    try {
      const hiddenHandler = () => resolveClose()
      normalizedElement.addEventListener('hidden.bs.modal', hiddenHandler)
      listeners.push({
        el: normalizedElement,
        event: 'hidden.bs.modal',
        handler: hiddenHandler,
      })

      const closeButtons = normalizedElement.querySelectorAll(
        'button[data-bs-dismiss="modal"], button[aria-label*="Close"], .btn-close, .modal-header .close, button i.fa-times, button i.fa-xmark'
      )
      closeButtons.forEach((btn) => {
        const clickHandler = () => setTimeout(resolveClose, 400)
        btn.addEventListener('click', clickHandler)
        listeners.push({
          el: btn as HTMLElement,
          event: 'click',
          handler: clickHandler,
        })
      })

      const allButtons = normalizedElement.querySelectorAll('button')
      allButtons.forEach((btn) => {
        const text = btn.textContent?.toLowerCase() || ''
        const isActionButton =
          text.includes('cancel') ||
          text.includes('save') ||
          text.includes('close') ||
          btn.classList.contains('btn-secondary')
        if (isActionButton) {
          const clickHandler = () => {
            setTimeout(() => {
              if (
                !document.contains(normalizedElement) ||
                (normalizedElement.classList.contains('modal') &&
                  !normalizedElement.classList.contains('show'))
              ) {
                resolveClose()
              }
            }, 500)
          }
          btn.addEventListener('click', clickHandler)
          listeners.push({
            el: btn as HTMLElement,
            event: 'click',
            handler: clickHandler,
          })
        }
      })

      const poll = () => {
        if (resolved) return
        try {
          if (!document.contains(normalizedElement)) {
            resolveClose()
            return
          }
          const style = window.getComputedStyle(normalizedElement)
          if (style.display === 'none' || style.visibility === 'hidden') {
            resolveClose()
            return
          }
          if (
            normalizedElement.classList.contains('modal') &&
            !normalizedElement.classList.contains('show')
          ) {
            resolveClose()
            return
          }
          if (normalizedElement.getAttribute('aria-hidden') === 'true') {
            resolveClose()
            return
          }
          const backdrop = document.querySelector('.modal-backdrop')
          if (!backdrop) {
            const anyModalOpen = detectOpenModal()
            if (!anyModalOpen) {
              resolveClose()
              return
            }
          }
          if (Date.now() - start > timeoutMs) {
            resolveTimeout()
            return
          }
        } catch (err) {
          calculateErrorMessage(err)
          resolveTimeout()
          return
        }
        setTimeout(poll, interval)
      }
      poll()
      setTimeout(() => resolveTimeout(), timeoutMs)
    } catch (err) {
      calculateErrorMessage(err)
      resolveTimeout()
    }
  })
}

export function setPortalPassThrough(passThrough: boolean) {
  try {
    // Persist state globally so newly-created portals pick it up
    ;(window as any).__walkthrough_portal_pass_through = !!passThrough

    const portal = document.getElementById('walkthrough-overlay-portal-root')
    if (!portal) return
    portal.style.pointerEvents = passThrough ? 'none' : 'auto'
    const backdrop = portal.querySelector('.walkthrough-backdrop') as HTMLElement | null
    if (backdrop) backdrop.style.pointerEvents = passThrough ? 'none' : 'auto'
  } catch (err) {
    calculateErrorMessage(err)
  }
}

export function ensurePortalInteractive() {
  try {
    const portal = document.getElementById('walkthrough-overlay-portal-root')
    if (portal) portal.style.pointerEvents = 'auto'
    const backdrop = portal?.querySelector('.walkthrough-backdrop') as HTMLElement | null
    if (backdrop) backdrop.style.pointerEvents = 'auto'
  } catch (err) {
    calculateErrorMessage(err)
  }
}
